/**
 * Invite actions script file
 *
 * @package    Actions
 * @author     Vadym Karpenko <vadym.karpenko@door3.com>
 */

import { SubmissionError } from 'redux-form';

import InviteApi from 'Api/invite';
import { decryptInvite } from 'Utils';
import {
    LOCATION_PREFIX,
} from 'Constants';

export const INVITE_ITEM_REQUEST = 'INVITE_ITEM_REQUEST';
export const INVITE_ITEM_SUCCESS = 'INVITE_ITEM_SUCCESS';
export const INVITE_ITEM_ERROR = 'INVITE_ITEM_ERROR';

export const INVITE_UPDATE_REQUEST = 'INVITE_UPDATE_REQUEST';
export const INVITE_UPDATE_SUCCESS = 'INVITE_UPDATE_SUCCESS';
export const INVITE_UPDATE_ERROR = 'INVITE_UPDATE_ERROR';
export const INVITE_UPD_ITEM = 'INVITE_UPD_ITEM';
export const INVITE_UPD_ITEM_REQUEST = 'INVITE_UPD_ITEM_REQUEST';
export const INVITE_UPD_ITEM_SUCCESS = 'INVITE_UPD_ITEM_SUCCESS';
export const INVITE_UPD_ITEM_ERROR = 'INVITE_UPD_ITEM_ERROR';
export const INVITES_SEND_REQUEST = 'INVITES_SEND_REQUEST';
export const INVITES_SEND_SUCCESS = 'INVITES_SEND_SUCCESS';
export const INVITES_SEND_ERROR = 'INVITES_SEND_ERROR';

export const INVITE_VERIFY_REQUEST = 'INVITE_VERIFY_REQUEST';
export const INVITE_VERIFY_SUCCESS = 'INVITE_VERIFY_SUCCESS';
export const INVITE_VERIFY_ERROR = 'INVITE_VERIFY_ERROR';

export const SET_PREVIEW_MODE = 'SET_PREVIEW_MODE';

/**
 * Set locale
 *
 * @param {Boolean} preview
 *
 * @returns {Object}
 */
export function setPreviewMode (preview) {
    return {
        preview,
        type: SET_PREVIEW_MODE,
    };
}

export function loadItem ({ groupID, inviteHash, preview }) {
    const [api, id, params] = groupID ?
        [
            InviteApi.loadTempItem,
            groupID,
            { preview },
        ] :
        [
            InviteApi.loadItem,
            inviteHash,
        ];

    return function (dispatch) {
        return dispatch({
            errorCallback: (response) => {
                const { error_description } = response;

                return {
                    error: error_description,
                };
            },
            request: api(id, params),
            requestType: 'INVITE_ITEM',
        });
    };
}

export function createItem (item) {
    const data = { ...item };

    data.guests = data.guests.map((guest) => prepareInviteData(guest));
    data.invite = {
        surveyAnswers: data.guests[0].surveyAnswers,
    };
    delete data.questions;

    return function (dispatch) {
        return dispatch({
            errorCallback: (data) => ({ message: data.data }),
            request: InviteApi.createItem(data),
            requestType: 'INVITE_UPDATE',
            successCallback: (data, dispatch) => {
                const { inviteHash } = decryptInvite(data.data);

                return dispatch({
                    request: InviteApi.loadItem(inviteHash),
                    requestType: 'INVITE_UPD_ITEM',
                });
            },
        });
    };
}

export function updateItem (inviteHash, item) {
    return function (dispatch) {
        return dispatch({
            request: InviteApi.updateItem(inviteHash, prepareInviteData(item)),
            requestType: 'INVITE_UPDATE',
            successCallback: (data, dispatch) => {
                return dispatch({
                    request: InviteApi.loadItem(inviteHash),
                    requestType: 'INVITE_UPD_ITEM',
                });
            },
        });
    };
}

export function updateItemLocal (data) {
    return {
        data,
        type: INVITE_UPD_ITEM,
    };
}

export function sendInvites (inviteHash, guestInfo) {
    const data = { guestInfo };

    data.guestInfo = data.guestInfo.map((guest) => prepareGuestData(guest)).map((guest) => ({
        ...guest,
        email: guest.email?.primary,
        nameFirst: guest.name?.first,
        nameLast: guest.name?.last,
    }));

    return function (dispatch) {
        return dispatch({
            errorCallback: (data, dispatch) => {
                dispatch({
                    type: INVITES_SEND_ERROR,
                });
                throw new SubmissionError();
            },
            request: InviteApi.sendInvites(inviteHash, data),
            requestType: 'INVITES_SEND',
            successCallback: () => ({
                data: { inviteHash },
                type: INVITES_SEND_SUCCESS,
            }),
        });
    };
}

export function verifyItem (data) {
    return function (dispatch) {
        return dispatch({
            errorCallback: (data, dispatch) => {
                dispatch({
                    type: INVITE_VERIFY_ERROR,
                });
                if (data.error_description) {
                    throw new SubmissionError(data.error_description);
                }
                if (data.data.isGuestRegistered) {
                    window.location.href = `${LOCATION_PREFIX}/permission?inviteHash=${data.data.inviteHash}`;
                }
            },
            request: InviteApi.verify(data),
            requestType: 'INVITE_VERIFY',
            successCallback: () => ({ data }),
        });
    };
}

const prepareInviteData = (item) => {
    let data = item;

    if (data.questions) {
        data.surveyAnswers = [];
        Object.keys(data.questions).forEach((question) => {
            data.surveyAnswers.push({
                answer: data.questions[question],
                question,
            });
        });
        delete data.questions;
    }
    if (data.guestInfo) {
        data.guestInfo = prepareGuestData(data.guestInfo);
    }

    return data;
};

const prepareGuestData = (item) => {
    let data = { ...item };

    if (data.phone && data.phone.sms) {
        data.phone = { ...data.phone };
        data.phone.sms = { ...data.phone.sms };
        data.phone.sms.isOptOut = data.phone.sms.isOptOut === 'yes' ? 0 : 1;
    }
    if (data.travelPreferences) {
        data.travelPreferences = { ...data.travelPreferences };
        data.travelPreferences.departureAirport =
            data.travelPreferences.departureAirport?.value;
        data.travelPreferences.frequentFlyer = {
            [data.travelPreferences.frequentFlyer?.program]: data.travelPreferences.frequentFlyer?.no,
        };
    }

    return data;
};
